import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Swords, Clock, Target, Trophy, Flame, Loader2, Send } from "lucide-react";
import UserSearch from "../components/UserSearch";
import { useAuth } from "../lib/auth";

const DURATIONS = [2, 3, 5];

export default function Battles() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState([]);
  const [creating, setCreating] = useState(null);
  const [msg, setMsg] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(null);

  const load = () => api.get("/battles/active").then(r => setActive(r.data));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const create = async (mins, isPk = false) => {
    setCreating(mins); setMsg("");
    try {
      const { data } = await api.post("/battles/create", { duration_minutes: mins, is_pk: isPk, target: isPk ? 5000 : null });
      setMsg(`${mins}-min ${isPk ? "PK" : ""} battle created — opponents can join now!`);
      load();
    } catch (e) { setMsg(formatErr(e)); }
    finally { setCreating(null); }
  };

  const join = async (id) => {
    try {
      await api.post(`/battles/${id}/join`);
      setMsg("Battle joined! May the best streamer win!");
      load();
    } catch (e) { setMsg(formatErr(e)); }
  };

  const inviteToBattle = async (target) => {
    setInviting(target.id);
    try {
      const subject = `Battle invite from @${user?.username || user?.name}`;
      const message = `Hi @${target.username}, ${user?.name || "I"} just challenged you to a Live Battle on Hifireels! Go Live now and tap "Join Battle" to accept.`;
      await api.post("/dm/send", { to_user_id: target.id, content: subject + "\n\n" + message });
      setMsg(`📨 Battle invite sent to @${target.username}!`);
      setShowInvite(false);
      setTimeout(() => setMsg(""), 3500);
    } catch (e) {
      setMsg(formatErr(e));
    } finally { setInviting(null); }
  };

  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <Swords className="w-7 h-7 text-[#FF7A00]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Battles</h1>
      </div>
      <p className="text-white/60 text-sm mb-4">Live battle other streamers • Most gifts received wins</p>

      <div className="p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 text-xs mb-4">
        ⚠️ You must be <b className="text-[#FF7A00]">currently live</b> to create or join battles. Go to Live → Go Live first.
      </div>

      {msg && <div className="mb-3 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="battle-msg">{msg}</div>}

      <div className="mb-5">
        <button onClick={() => setShowInvite(s => !s)} data-testid="invite-opponent-toggle"
          className="w-full p-3 rounded-2xl bg-gradient-to-r from-[#FF7A00]/15 to-[#FF2A5F]/15 border border-[#FF7A00]/30 flex items-center justify-between active:scale-[0.98]">
          <span className="flex items-center gap-2 font-bold text-sm"><Send className="w-4 h-4 text-[#FF7A00]"/>Invite a user to Battle</span>
          <span className="text-xs text-white/60">{showInvite ? "Hide" : "Search"}</span>
        </button>
        {showInvite && (
          <div className="mt-2 rounded-2xl bg-white/5 border border-white/10 p-3" data-testid="invite-opponent-panel">
            <p className="text-[11px] text-white/60 mb-2">Search by name, @username or email. Tap to send a battle invite via DM.</p>
            <UserSearch
              onPick={inviteToBattle}
              pickLabel={inviting ? "Sending..." : "Invite"}
              excludeUserIds={user ? [user.id] : []}
              autoFocus
              placeholder="Search streamer to invite..."
            />
          </div>
        )}
      </div>

      <h2 className="font-outfit font-bold text-lg mb-2">Quick Battles</h2>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {DURATIONS.map(m => (
          <button key={m} onClick={()=>create(m)} disabled={creating===m} data-testid={`create-battle-${m}`}
            className="p-4 rounded-2xl bg-gradient-to-br from-[#FF2A5F]/20 to-[#00E5FF]/20 border border-white/10 active:scale-95 disabled:opacity-50">
            <Clock className="w-5 h-5 text-[#FF2A5F] mx-auto mb-1"/>
            <div className="font-outfit font-extrabold text-2xl">{m}m</div>
            <div className="text-[10px] text-white/60 uppercase">Battle</div>
          </button>
        ))}
      </div>

      <h2 className="font-outfit font-bold text-lg mb-2 flex items-center gap-2">
        <Target className="w-4 h-4 text-[#A855F7]"/>PK Battle (Monthly)
      </h2>
      <button onClick={()=>create(30, true)} disabled={creating===30} data-testid="create-pk"
        className="w-full p-4 rounded-3xl bg-gradient-to-br from-[#A855F7]/30 via-[#FF2A5F]/20 to-[#FF7A00]/20 border border-[#A855F7]/30 active:scale-95 disabled:opacity-50 mb-5">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="font-outfit font-extrabold text-xl">30-min PK Battle</div>
            <div className="text-xs text-white/70">Hit 5,000 Goldly target to win</div>
          </div>
          <Trophy className="w-8 h-8 text-[#FF7A00]"/>
        </div>
      </button>

      <h2 className="font-outfit font-bold text-lg mb-2 flex items-center gap-2">
        <Flame className="w-4 h-4 text-[#FF2A5F]"/>Live Battles
      </h2>
      <div className="space-y-2" data-testid="active-battles">
        {active.length === 0 && <p className="text-center text-white/40 text-sm py-6">No active battles right now</p>}
        {active.map(b => (
          <div key={b.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`battle-${b.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF2A5F]/20 text-[#FF2A5F] font-bold">
                {b.is_pk ? "PK Battle" : "Battle"} • {b.duration_minutes}m
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${b.status==="active"?"bg-emerald-500/20 text-emerald-400":"bg-[#FF7A00]/20 text-[#FF7A00]"}`}>{b.status}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Link to={`/u/${b.host?.id}`} className="flex items-center gap-2 flex-1">
                <img src={b.host?.avatar} className="w-8 h-8 rounded-full"/>
                <span className="text-xs font-bold truncate">@{b.host?.username}</span>
              </Link>
              <span className="font-outfit font-extrabold text-sm text-[#FF2A5F]">VS</span>
              {b.opponent ? (
                <Link to={`/u/${b.opponent?.id}`} className="flex items-center gap-2 flex-1 justify-end">
                  <span className="text-xs font-bold truncate">@{b.opponent?.username}</span>
                  <img src={b.opponent?.avatar} className="w-8 h-8 rounded-full"/>
                </Link>
              ) : (
                <button onClick={()=>join(b.id)} data-testid={`join-${b.id}`} className="px-3 py-1.5 rounded-full bg-[#00E5FF] text-black text-xs font-bold">Join</button>
              )}
            </div>
            {b.status === "active" && (
              <div className="mt-2 text-[10px] text-white/60 flex justify-between">
                <span>{b.host?.username}: <b className="text-[#FF7A00]">{b.host_score}</b></span>
                <span>{b.opponent?.username}: <b className="text-[#FF7A00]">{b.opponent_score}</b></span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
